import { faPlus, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { zodResolver } from "@hookform/resolvers/zod"
import {
	CompanyConsumerDetails,
	ExternalIntegrationInformation,
	GetConsumer,
	PrivateConsumerDetails,
} from "CustomerPortal/CustomerPortalConsumerInformation/CustomerPortalConsumerInformation"
import { exhaustive } from "exhaustive"
import { API, ServerError } from "network/API"
import { FinalizeButton } from "Orders/Components/Form/Buttons/Buttons"
import { ModulePopup } from "Orders/Components/ModulePopup/ModulePopup"
import { SbtH2 } from "Orders/Components/Text/SbtH2/SbtH2"
import { SbtH4 } from "Orders/Components/Text/SbtH4/SbtH4"
import { SbtRHFError } from "Orders/Components/Text/SbtInvalid/SbtRHFError"
import { FC, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { useClient } from "../../Client/ClientAndUserProvider"
import { useNavigator } from "../../Navigator/useNavigator"
import {
	invalidOrganizationNumberMessage,
	invalidPersonNumberMessage,
	invalidPhoneNumberMessage,
	optionalTextInput,
	organizationNumberRegex,
	personNumberRegex,
	validatePhoneNumberForSweden,
} from "../../Orders/Components/Form/constants"
import orderConfirmStyle from "../../Orders/Components/OrderConfirmCustomerInformation/OrderConfirmCustomerInformation.module.css"
import { SbtH3 } from "../../Orders/Components/Text/SbtH3/SbtH3"
import { SbtInvalid } from "../../Orders/Components/Text/SbtInvalid/SbtInvalid"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { cls } from "../../Shared/cls"
import { LabelledToggle } from "../../Shared/LabelledToggle/LabelledToggle"
import style from "./ClientPortalNewConsumerModal.module.css"

enum NewConsumerType {
	Company = "Company",
	PrivatePerson = "PrivatePerson",
}

export enum CoredinationCustomerInvoiceDeliveryMethod {
	LETTER = "LETTER",
	EMAIL = "EMAIL",
	SMS = "SMS",
	EINVOICE = "EINVOICE",
	PRINTSERVICE = "PRINTSERVICE",
	CASHINVOICE = "CASHINVOICE",
	MANUAL = "MANUAL",
}

const PrivateSpecificDetailsSchema = z.object({
	type: z.literal(NewConsumerType.PrivatePerson),
	name: z.string().min(1),
	personNumber: z.string().min(1, "Ett personnummer krävs").regex(personNumberRegex, invalidPersonNumberMessage),
})

const BusinessSpecificDetailsSchema = z.object({
	type: z.literal(NewConsumerType.Company),
	name: z.string().min(1),
	orgNumber: z
		.string()
		.min(1, "Ett organisationsnummer krävs")
		.regex(organizationNumberRegex, invalidOrganizationNumberMessage),
})

const ExternalReadOnlyCoredinationInformationSchema = z.object({
	type: z.literal("ReadOnlyCoredination"),
	invoiceDeliveryMethod: z.nativeEnum(CoredinationCustomerInvoiceDeliveryMethod).nullish(),
	termsOfPaymentDays: z.number().nullish(),
})

const NewConsumerDetailsSchema = z.object({
	data: z.discriminatedUnion("type", [PrivateSpecificDetailsSchema, BusinessSpecificDetailsSchema]),
	externalInformation: z.discriminatedUnion("type", [ExternalReadOnlyCoredinationInformationSchema]).optional(),

	invoiceEmail: optionalTextInput(z.string().email(), "Felaktigt format på e-post"),
	invoiceStreet: z.string().optional(),
	invoiceCity: z.string().optional(),
	invoiceZipCode: z.string().optional(),
	invoiceContactName: z.string().optional(),
	invoiceContactPhoneNumber: z
		.string()
		.optional()
		.transform((x) => (!x ? null : x))
		.refine(
			(data) => {
				if (data === null) {
					return true
				}
				return validatePhoneNumberForSweden(data)
			},
			{ message: invalidPhoneNumberMessage },
		),
})

type PrivateSpecificDetails = z.input<typeof PrivateSpecificDetailsSchema>
type BusinessSpecificDetails = z.input<typeof BusinessSpecificDetailsSchema>
type NewConsumerDetailsSchemaType = z.input<typeof NewConsumerDetailsSchema>

export type Props = {
	onClose?: () => void
	onAdd: (consumer: GetConsumer) => void
	externalButton?: boolean
	showModal?: boolean
	setToggleToCreateIndividual?: boolean
	newConsumerButtonClass?: string
}

type CreateConsumerResponse = {
	consumer: GetConsumer
}

enum CreateConsumerErrorState {
	ConsumerAlreadyExists = "consumer_already_exists",
	RegularError = "RegularError",
}

type CreateConsumerErrorRegular = {
	type: CreateConsumerErrorState.RegularError
}

type CreateConsumerErrorConsumerAlreadyExists = {
	type: CreateConsumerErrorState.ConsumerAlreadyExists
	consumerId: string
}

type CreateConsumerError = CreateConsumerErrorRegular | CreateConsumerErrorConsumerAlreadyExists

export function coredinationInvoiceDeliveryMethodToHumanText(deliveryMethod: CoredinationCustomerInvoiceDeliveryMethod) {
	return exhaustive(deliveryMethod, {
		LETTER: () => "Brev",
		EMAIL: () => "E-post",
		SMS: () => "SMS",
		EINVOICE: () => "E-faktura",
		MANUAL: () => "Manuell",
		PRINTSERVICE: () => deliveryMethod,
		CASHINVOICE: () => deliveryMethod,
		_: () => deliveryMethod,
	})
}

export type SetConsumerBasicInvoicingInformation = {
	type: "Basic"
	email?: string | null
	address?: SetConsumerInvoicingAddress | null
	contactPerson?: SetConsumerInvoicingContact | null
}

export type SetConsumerInvoicingAddress = {
	street?: string | null
	city?: string | null
	zipCode?: string | null
}

export type SetConsumerInvoicingContact = {
	name?: string | null
	phoneNumber?: string | null
}

export type SetConsumerInvoicingInformation = SetConsumerBasicInvoicingInformation

export const ClientPortalNewConsumerModal: FC<Props> = ({
	onAdd,
	externalButton,
	showModal: extShowModal,
	onClose: extOnClose,
	setToggleToCreateIndividual,
	newConsumerButtonClass,
}) => {
	const client = useClient()
	const permissions = usePermissions()
	const navigator = useNavigator()

	const formRef = useRef<HTMLFormElement | null>(null)

	const [showModal, setShowModal] = useState<boolean>(false)

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
	const [errorSubmitting, setErrorSubmitting] = useState<CreateConsumerError | null>(null)

	const {
		register,
		handleSubmit,
		reset,
		getValues,
		watch,
		setValue,
		formState: { errors, isValid },
	} = useForm<NewConsumerDetailsSchemaType>({
		mode: "onTouched",
		resolver: async (data, context, options) => {
			return zodResolver(NewConsumerDetailsSchema)(data, context, options)
		},
	})

	function changeConsumerType(type: NewConsumerType) {
		exhaustive(type, {
			Company: () => {
				setValue("data", {
					type: NewConsumerType.Company,
					name: "",
					orgNumber: "",
				})
			},
			PrivatePerson: () => {
				setValue("data", {
					type: NewConsumerType.PrivatePerson,
					name: "",
					personNumber: "",
				})
			},
		})
	}

	useEffect(() => {
		if (!showModal) {
			setErrorSubmitting(null)
			reset()
		}
	}, [showModal])

	useEffect(() => {
		setShowModal(!!extShowModal)
	}, [extShowModal])

	useEffect(() => {
		if (!setToggleToCreateIndividual) {
			changeConsumerType(NewConsumerType.Company)
		} else {
			changeConsumerType(NewConsumerType.PrivatePerson)
		}
	}, [setToggleToCreateIndividual])

	function toggleNewConsumerType() {
		if (isSubmitting) {
			return
		}

		const type = watch("data")?.type
		const consumerType = !!type ? type : NewConsumerType.Company

		exhaustive(consumerType, {
			Company: () => {
				changeConsumerType(NewConsumerType.PrivatePerson)
			},
			PrivatePerson: () => {
				changeConsumerType(NewConsumerType.Company)
			},
			_: () => {},
		})
	}

	function form() {
		const type = watch("data")?.type
		const consumerType = !!type ? type : NewConsumerType.Company

		return (
			<>
				<form
					key={`form_newConsumer_${consumerType}`}
					id="form_newConsumer"
					ref={formRef}
					onSubmit={handleSubmit(onSubmit)}>
					{formConsumerInputFields(consumerType)}
					{formConsumerInvoicingInformationInputFields()}
					{externalIntegrationCoredinationFields()}
					{submitErrorSection()}
				</form>
				<FinalizeButton
					style={{
						marginTop: "10px",
						minHeight: "42px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: "10px",
					}}
					disabled={!isValid || isSubmitting}
					onClick={() => {
						formRef.current?.requestSubmit()
					}}>
					Spara
					{isSubmitting ? <FontAwesomeIcon style={{ color: "gray" }} spin={true} icon={faSpinner} /> : null}
				</FinalizeButton>
			</>
		)
	}

	function formConsumerInputFields(type: NewConsumerType) {
		return exhaustive(type, {
			Company: () => {
				return organizationInputFields()
			},
			PrivatePerson: () => {
				return individualInputFields()
			},
		})
	}

	function formConsumerInvoicingInformationInputFields() {
		return (
			<>
				<hr style={{ width: "100%", marginBottom: "20px" }} />
				<SbtH3 styles={{ marginBottom: "20px", fontWeight: 600 }}>Faktureringsuppgifter</SbtH3>
				<label>
					<SbtH4>E-post</SbtH4>
					<input
						disabled={isSubmitting}
						className={orderConfirmStyle.input}
						{...register("invoiceEmail")}
						placeholder="exempel@exempel.se"
					/>
					<SbtRHFError error={errors?.invoiceEmail} />
				</label>
				<label>
					<SbtH4>Gata</SbtH4>
					<input
						disabled={isSubmitting}
						className={orderConfirmStyle.input}
						{...register("invoiceStreet")}
						placeholder="Gatan 3"
					/>
					<SbtRHFError error={errors.invoiceStreet} />
				</label>
				<label>
					<SbtH4>Postnummer</SbtH4>
					<input
						disabled={isSubmitting}
						className={orderConfirmStyle.input}
						{...register("invoiceZipCode")}
						placeholder="123 45"
					/>
					<SbtRHFError error={errors.invoiceZipCode} />
				</label>
				<label>
					<SbtH4>Postort</SbtH4>
					<input
						disabled={isSubmitting}
						className={orderConfirmStyle.input}
						{...register("invoiceCity")}
						placeholder="Stockholm"
					/>
					<SbtRHFError error={errors.invoiceCity} />
				</label>
				<label>
					<SbtH4>Kontaktperson namn</SbtH4>
					<input
						disabled={isSubmitting}
						className={orderConfirmStyle.input}
						{...register("invoiceContactName")}
						placeholder="Anders Andersson"
					/>
					<SbtRHFError error={errors.invoiceContactName} />
				</label>
				<label>
					<SbtH4>Kontaktperson telefon</SbtH4>
					<input
						disabled={isSubmitting}
						className={orderConfirmStyle.input}
						{...register("invoiceContactPhoneNumber")}
						placeholder="070 XXX XX XX"
					/>
					<SbtRHFError error={errors.invoiceContactPhoneNumber} />
				</label>
			</>
		)
	}

	function externalIntegrationCoredinationFields() {
		if (!client.hasFullCoredinationIntegrationEnabled()) {
			return null
		}

		if (!getValues("externalInformation.type") || getValues("externalInformation.type") !== "ReadOnlyCoredination") {
			setValue("externalInformation.type", "ReadOnlyCoredination")
			setValue("externalInformation.termsOfPaymentDays", 7)
		}

		return (
			<>
				<hr style={{ width: "100%", marginBottom: "20px" }} />
				<SbtH4>
					Dessa fält kommer från en extern integrering till Coredination, de gör för närvarande ingenting hos
					Skrappy.
				</SbtH4>

				<label>
					<SbtH4>Antal dagar för betalning</SbtH4>
					<input
						disabled={isSubmitting}
						className={style.newConsumerModalInputField}
						{...register("externalInformation.termsOfPaymentDays", { valueAsNumber: true })}
					/>
					<SbtRHFError error={(errors as any)?.externalInformation?.termsOfPaymentDays} />
				</label>

				<label>
					<SbtH4>Leveransmetod för faktura?</SbtH4>
					<select
						className={orderConfirmStyle.input}
						disabled={isSubmitting}
						{...register("externalInformation.invoiceDeliveryMethod")}>
						<option value={CoredinationCustomerInvoiceDeliveryMethod.LETTER}>
							{coredinationInvoiceDeliveryMethodToHumanText(CoredinationCustomerInvoiceDeliveryMethod.LETTER)}
						</option>
						<option value={CoredinationCustomerInvoiceDeliveryMethod.EMAIL}>
							{coredinationInvoiceDeliveryMethodToHumanText(CoredinationCustomerInvoiceDeliveryMethod.EMAIL)}
						</option>
						<option value={CoredinationCustomerInvoiceDeliveryMethod.SMS}>
							{coredinationInvoiceDeliveryMethodToHumanText(CoredinationCustomerInvoiceDeliveryMethod.SMS)}
						</option>
						<option value={CoredinationCustomerInvoiceDeliveryMethod.EINVOICE}>
							{coredinationInvoiceDeliveryMethodToHumanText(
								CoredinationCustomerInvoiceDeliveryMethod.EINVOICE,
							)}
						</option>
						<option value={CoredinationCustomerInvoiceDeliveryMethod.MANUAL}>
							{coredinationInvoiceDeliveryMethodToHumanText(CoredinationCustomerInvoiceDeliveryMethod.MANUAL)}
						</option>
					</select>
					<SbtRHFError error={(errors as any)?.externalInformation?.invoiceDeliveryMethod} />
				</label>
			</>
		)
	}

	function organizationInputFields() {
		return (
			<>
				<label>
					<SbtH4>Företagsnamn*</SbtH4>
					<input
						disabled={isSubmitting}
						className={style.newConsumerModalInputField}
						{...register("data.name")}
						placeholder="Fyll i företagsnamn"
					/>
					<SbtRHFError error={errors.data?.name} />
				</label>
				<label>
					<SbtH4>Organisationsnummer*</SbtH4>
					<input
						disabled={isSubmitting}
						className={style.newConsumerModalInputField}
						{...register("data.orgNumber")}
						placeholder="112233-XXXX"
					/>
					<SbtRHFError error={(errors.data as any)?.orgNumber} />
				</label>
			</>
		)
	}

	function individualInputFields() {
		return (
			<>
				<label>
					<SbtH4>Namn*</SbtH4>
					<input
						disabled={isSubmitting}
						className={style.newConsumerModalInputField}
						{...register("data.name")}
						placeholder="Fyll i namn"
					/>
					<SbtRHFError error={errors.data?.name} />
				</label>
				<label>
					<SbtH4>Personnummer*</SbtH4>
					<input
						disabled={isSubmitting}
						className={style.newConsumerModalInputField}
						{...register("data.personNumber")}
						placeholder="ÅÅÅÅMMDD-XXXX"
					/>
					<SbtRHFError error={(errors.data as any)?.personNumber} />
				</label>
			</>
		)
	}

	function compileConsumerDetails(
		data: PrivateSpecificDetails | BusinessSpecificDetails,
	): CompanyConsumerDetails | PrivateConsumerDetails | null {
		if ("personNumber" in data) {
			return {
				type: "PrivateConsumerDetails",
				name: data.name,
				personNumber: data.personNumber,
			}
		} else if ("orgNumber" in data) {
			return {
				type: "CompanyConsumerDetails",
				name: data.name,
				orgNumber: data.orgNumber,
			}
		}

		return null
	}

	function maybeGetInvoiceInformation(): SetConsumerInvoicingInformation | null {
		if (
			getValues("invoiceEmail") ||
			getValues("invoiceStreet") ||
			getValues("invoiceCity") ||
			getValues("invoiceZipCode") ||
			getValues("invoiceContactName") ||
			getValues("invoiceContactPhoneNumber")
		) {
			return {
				type: "Basic",
				email: getValues("invoiceEmail") || null,
				address:
					getValues("invoiceStreet") || getValues("invoiceCity") || getValues("invoiceZipCode")
						? {
								street: getValues("invoiceStreet") || null,
								city: getValues("invoiceCity") || null,
								zipCode: getValues("invoiceZipCode") || null,
						  }
						: null,
				contactPerson:
					getValues("invoiceContactName") || getValues("invoiceContactPhoneNumber")
						? {
								name: getValues("invoiceContactName"),
								phoneNumber: getValues("invoiceContactPhoneNumber"),
						  }
						: null,
			}
		}

		return null
	}

	async function onSubmit(formData: NewConsumerDetailsSchemaType) {
		setIsSubmitting(true)

		const consumerDetails = compileConsumerDetails(formData.data)
		if (!consumerDetails) {
			return
		}
		type SetConsumer = {
			consumerDetails: CompanyConsumerDetails | PrivateConsumerDetails
			externalInformation?: ExternalIntegrationInformation
			invoicingInformation?: SetConsumerInvoicingInformation | null
		}
		type CreateConsumerRequest = {
			consumer: SetConsumer
		}
		const postBody: CreateConsumerRequest = {
			consumer: {
				consumerDetails: consumerDetails,
				externalInformation: formData.externalInformation,
				invoicingInformation: maybeGetInvoiceInformation(),
			},
		}

		return API.postWithRetries<CreateConsumerResponse, CreateConsumerRequest>(
			`/customer-portal/consumer-manager-v1/${client.identifier}`,
			postBody,
		)
			.then((res: CreateConsumerResponse) => {
				setIsSubmitting(false)
				onAdd(res.consumer)
				reset()
				setShowModal(false)
				if (extOnClose) {
					extOnClose()
				}
			})
			.catch((error: ServerError<any>) => {
				setIsSubmitting(false)
				if (
					error?.data?.message &&
					error.data.message?.startsWith(CreateConsumerErrorState.ConsumerAlreadyExists)
				) {
					setErrorSubmitting({
						type: CreateConsumerErrorState.ConsumerAlreadyExists,
						consumerId: error?.data?.message?.split("|")?.[1] || "",
					})
				} else {
					setErrorSubmitting({ type: CreateConsumerErrorState.RegularError })
				}
			})
	}

	function submitErrorSection() {
		if (!errorSubmitting) {
			return null
		}

		return exhaustive(errorSubmitting, "type", {
			[CreateConsumerErrorState.RegularError]: () => {
				return <SbtInvalid styles={{ margin: "10px 0" }}>Något gick fel, vänligen försök igen</SbtInvalid>
			},
			[CreateConsumerErrorState.ConsumerAlreadyExists]: (err) => {
				return (
					<SbtInvalid styles={{ margin: "10px 0" }}>
						Kunden du försöker skapa finns redan registrerad i vårt system. Klicka{" "}
						<span
							className={style.alreadyExistingConsumerLink}
							onClick={() => {
								navigator.open(`my-pages/consumers?tab=overview&consumerId=${err.consumerId}`)
							}}>
							här
						</span>{" "}
						för att navigera till kunden
					</SbtInvalid>
				)
			},
		})
	}

	if (!permissions.isAllowed(PermissionAreaLocation.Client_Consumers_Create)) {
		return null
	}

	return (
		<div>
			{!externalButton ? (
				<button onClick={() => setShowModal(true)} className={cls(style.newConsumerButton, newConsumerButtonClass)}>
					<div className={style.newConsumerButtonContent}>
						<span className={style.newConsumerButtonText}>Ny kund</span>
						<FontAwesomeIcon icon={faPlus} />
					</div>
				</button>
			) : null}
			{showModal ? (
				<ModulePopup
					disableClose={isSubmitting}
					className={style.modal}
					show={showModal}
					onClose={() => {
						reset()
						setShowModal(false)
						if (extOnClose) {
							extOnClose()
						}
					}}>
					<div className={style.wrapper}>
						<div className={style.newConsumerModalHeader}>
							<SbtH2>Skapa en ny kund</SbtH2>
						</div>
						<div style={{ marginTop: "20px" }}>
							<LabelledToggle
								disabled={isSubmitting}
								currentValue={getValues("data.type") === NewConsumerType.PrivatePerson}
								onChange={toggleNewConsumerType}
								trueLabel="Privatperson"
								falseLabel="Företag"
							/>
						</div>
						{form()}
					</div>
				</ModulePopup>
			) : null}
		</div>
	)
}
